import { dashboardT } from '../../../../lib/messages';
import type { TargetingFormValue } from '../../model';
import type { TargetingFormUpdate } from './TargetingForm.types';
import { InclusionToggle } from './InclusionToggle';
import { TargetingSection } from './TargetingSection';
import { TargetingTextarea } from './TargetingTextarea/TargetingTextarea';

type ContextualTextField = Readonly<{
  includedKey:
    | 'contextualChannelGroupsIncluded'
    | 'contextualChannelsIncluded'
    | 'contextualContentTitlesIncluded'
    | 'contextualContentKeywordsIncluded'
    | 'contextualContentGenresIncluded';
  labelKey:
    | 'targeting.contextual.channelGroups'
    | 'targeting.contextual.channels'
    | 'targeting.contextual.contentTitles'
    | 'targeting.contextual.contentKeywords'
    | 'targeting.contextual.contentGenres';
  textKey:
    | 'contextualChannelGroupsText'
    | 'contextualChannelsText'
    | 'contextualContentTitlesText'
    | 'contextualContentKeywordsText'
    | 'contextualContentGenresText';
}>;

const contextualTextFields: readonly ContextualTextField[] = [
  {
    includedKey: 'contextualChannelGroupsIncluded',
    labelKey: 'targeting.contextual.channelGroups',
    textKey: 'contextualChannelGroupsText',
  },
  {
    includedKey: 'contextualChannelsIncluded',
    labelKey: 'targeting.contextual.channels',
    textKey: 'contextualChannelsText',
  },
  {
    includedKey: 'contextualContentTitlesIncluded',
    labelKey: 'targeting.contextual.contentTitles',
    textKey: 'contextualContentTitlesText',
  },
  {
    includedKey: 'contextualContentKeywordsIncluded',
    labelKey: 'targeting.contextual.contentKeywords',
    textKey: 'contextualContentKeywordsText',
  },
  {
    includedKey: 'contextualContentGenresIncluded',
    labelKey: 'targeting.contextual.contentGenres',
    textKey: 'contextualContentGenresText',
  },
];

export function ContextualTargetingSection({
  updateField,
  value,
}: {
  updateField: TargetingFormUpdate;
  value: TargetingFormValue;
}) {
  return (
    <TargetingSection title={dashboardT('targeting.contextual.title')}>
      <div className="grid gap-6">
        {contextualTextFields.map((field) => (
          <ContextualTextArea
            key={field.textKey}
            field={field}
            updateField={updateField}
            value={value}
          />
        ))}
      </div>
    </TargetingSection>
  );
}

function ContextualTextArea({
  field,
  updateField,
  value,
}: {
  field: ContextualTextField;
  updateField: TargetingFormUpdate;
  value: TargetingFormValue;
}) {
  const label = dashboardT(field.labelKey);
  return (
    <div className="grid gap-3 border-t pt-5 first:border-t-0 first:pt-0">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-foreground text-sm font-medium">{label}</span>
        <InclusionToggle
          included={value[field.includedKey]}
          onIncludedChange={(included) => {
            updateField(field.includedKey, included);
          }}
        />
      </div>
      <TargetingTextarea
        aria-label={label}
        copyLabel={label}
        value={value[field.textKey]}
        placeholder={dashboardT('targeting.contextual.listPlaceholder', { name: label })}
        onChange={(event) => {
          updateField(field.textKey, event.target.value);
        }}
      />
    </div>
  );
}
